/**
 * AccessibilityService - Font size, high contrast, and voice input controls
 *
 * Provides ADA/WCAG accessibility features for patient-facing pages:
 * - Font size adjustment (small/medium/large/x-large)
 * - High contrast mode
 * - Speech-to-text for form inputs
 *
 * Settings persist in localStorage.
 */

export interface AccessibilitySettings {
  fontSize: 'small' | 'medium' | 'large' | 'x-large';
  highContrast: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontSize: 'medium',
  highContrast: false,
};

const FONT_SIZE_MAP = {
  small: '14px',
  medium: '16px',
  large: '18px',
  'x-large': '20px',
};

const FONT_SIZE_ORDER: AccessibilitySettings['fontSize'][] = ['small', 'medium', 'large', 'x-large'];

class AccessibilityServiceClass {
  private settings: AccessibilitySettings = { ...DEFAULT_SETTINGS };
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadSettings();
    this.applySettings();
  }

  private loadSettings() {
    try {
      const saved = localStorage.getItem('accessibilitySettings');
      if (saved) {
        this.settings = { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
      }
    } catch {
      // Use defaults
    }
  }

  private saveSettings() {
    localStorage.setItem('accessibilitySettings', JSON.stringify(this.settings));
    this.applySettings();
    this.notifyListeners();
  }

  private applySettings() {
    if (typeof document === 'undefined') return;

    // Apply font size to root
    document.documentElement.style.fontSize = FONT_SIZE_MAP[this.settings.fontSize];

    // Apply high contrast
    if (this.settings.highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
  }

  getSettings(): AccessibilitySettings {
    return { ...this.settings };
  }

  // Font size controls
  increaseFontSize() {
    const currentIndex = FONT_SIZE_ORDER.indexOf(this.settings.fontSize);
    if (currentIndex < FONT_SIZE_ORDER.length - 1) {
      this.settings.fontSize = FONT_SIZE_ORDER[currentIndex + 1];
      this.saveSettings();
    }
  }

  decreaseFontSize() {
    const currentIndex = FONT_SIZE_ORDER.indexOf(this.settings.fontSize);
    if (currentIndex > 0) {
      this.settings.fontSize = FONT_SIZE_ORDER[currentIndex - 1];
      this.saveSettings();
    }
  }

  getFontSizeLabel(): string {
    return this.settings.fontSize.charAt(0).toUpperCase() + this.settings.fontSize.slice(1);
  }

  canIncrease(): boolean {
    return FONT_SIZE_ORDER.indexOf(this.settings.fontSize) < FONT_SIZE_ORDER.length - 1;
  }

  canDecrease(): boolean {
    return FONT_SIZE_ORDER.indexOf(this.settings.fontSize) > 0;
  }

  // High contrast
  toggleHighContrast() {
    this.settings.highContrast = !this.settings.highContrast;
    this.saveSettings();
    return this.settings.highContrast;
  }

  isHighContrast(): boolean {
    return this.settings.highContrast;
  }

  // Subscribe to settings changes (for React re-renders)
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach(l => l());
  }

  // Speech-to-text support check
  isSpeechRecognitionSupported(): boolean {
    if (typeof window === 'undefined') return false;
    return !!(
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition
    );
  }

  /**
   * Start speech recognition and return text via callback.
   * Returns a stop function, or null if not supported.
   */
  startSpeechRecognition(
    onResult: (text: string) => void,
    onEnd?: () => void,
    language: string = 'en-US'
  ): (() => void) | null {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = language;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      onResult(transcript);
    };

    recognition.onend = () => {
      onEnd?.();
    };

    recognition.onerror = (event: any) => {
      console.warn('[Accessibility] Speech recognition error:', event.error);
      onEnd?.();
    };

    recognition.start();
    return () => recognition.stop();
  }
}

// Singleton
export const AccessibilityService = new AccessibilityServiceClass();
